/**
 * Stats section for UserProfileModal.
 * Renders the parlays/legs toggle and the corresponding stats grid.
 */

import { PrivateValue } from '@components/ui/PrivateValue';
import { formatROI, formatStreak } from '@parlay-picker/shared/formatting';
import { getStreakColor } from '@utils/colorUtils';
import { BarChart3, Layers } from 'lucide-react';
import type { ReactElement } from 'react';

type AnalysisLevel = 'legs' | 'parlays';

interface OverallStats {
  roi: number;
  winRate: number;
  wins: number;
  losses: number;
  pushes: number;
  totalBets: number;
  totalProfit?: number | null;
  totalUnits?: number | null;
  currentStreak: number;
  avgOdds: number;
}

interface LegStats {
  legHitRate: number;
  legsHit: number;
  legsMissed: number;
  totalLegs: number;
  overHitRate: number;
  oversHit: number;
  oversTotal: number;
  underHitRate: number;
  undersHit: number;
  undersTotal: number;
}

export interface ProfileStatsSectionProps {
  level: AnalysisLevel;
  onLevelChange: (level: AnalysisLevel) => void;
  overallStats: OverallStats;
  /** Null on the limited public profile, which shows parlay stats only. */
  legStats: LegStats | null;
  canSeeFinancialDetails: boolean;
}

export function ProfileStatsSection({
  level,
  onLevelChange,
  overallStats,
  legStats,
  canSeeFinancialDetails,
}: ProfileStatsSectionProps): ReactElement {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-theme-text-secondary uppercase tracking-wide">
          {level === 'legs' ? 'Leg Stats' : 'Overall Stats'}
        </h4>
        {legStats && (
          <div className="flex items-center bg-theme-surface/50 rounded-lg p-0.5">
            <button
              type="button"
              onClick={() => onLevelChange('parlays')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                level === 'parlays'
                  ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                  : 'text-theme-text-secondary hover:text-theme-text'
              }`}
            >
              <Layers className="w-3.5 h-3.5" />
              Parlays
            </button>
            <button
              type="button"
              onClick={() => onLevelChange('legs')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                level === 'legs'
                  ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                  : 'text-theme-text-secondary hover:text-theme-text'
              }`}
            >
              <BarChart3 className="w-3.5 h-3.5" />
              Legs
            </button>
          </div>
        )}
      </div>

      {level === 'parlays' || !legStats ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-theme-surface/50 rounded-lg p-4">
            <div className="text-xs text-theme-text-secondary mb-1">ROI</div>
            <div
              className={`text-2xl font-bold ${overallStats.roi >= 0 ? 'text-theme-success' : 'text-theme-danger'}`}
            >
              {formatROI(overallStats.roi)}
            </div>
          </div>
          <div className="bg-theme-surface/50 rounded-lg p-4">
            <div className="text-xs text-theme-text-secondary mb-1">Win Rate</div>
            <div className="text-2xl font-bold text-gray-100">
              {overallStats.winRate.toFixed(1)}%
            </div>
          </div>
          <div className="bg-theme-surface/50 rounded-lg p-4">
            <div className="text-xs text-theme-text-secondary mb-1">Record</div>
            <div className="text-lg font-semibold text-gray-100">
              {overallStats.wins}W - {overallStats.losses}L
            </div>
            <div className="text-xs text-theme-text-tertiary mt-1">
              {overallStats.pushes}P · {overallStats.totalBets} total
            </div>
          </div>
          <div className="bg-theme-surface/50 rounded-lg p-4">
            <div className="text-xs text-theme-text-secondary mb-1">Profit</div>
            {canSeeFinancialDetails && overallStats.totalProfit != null ? (
              <div
                className={`text-2xl font-bold ${overallStats.totalProfit >= 0 ? 'text-theme-success' : 'text-theme-danger'}`}
              >
                ${overallStats.totalProfit >= 0 ? '+' : ''}
                {overallStats.totalProfit.toFixed(0)}
              </div>
            ) : (
              <PrivateValue className="mt-1" />
            )}
            {/* Units show whether or not the profit does (Bryan, 2026-11-03, q-676-units-public) */}
            {overallStats.totalUnits != null && (
              <div
                className={`text-xs mt-1 ${overallStats.totalUnits >= 0 ? 'text-theme-success/70' : 'text-theme-danger/70'}`}
              >
                {overallStats.totalUnits >= 0 ? '+' : ''}
                {overallStats.totalUnits.toFixed(2)} units
              </div>
            )}
          </div>
          <div className="bg-theme-surface/50 rounded-lg p-4">
            <div className="text-xs text-theme-text-secondary mb-1">Current Streak</div>
            <div className={`text-2xl font-bold ${getStreakColor(overallStats.currentStreak)}`}>
              {formatStreak(overallStats.currentStreak)}
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-theme-surface/50 rounded-lg p-4">
            <div className="text-xs text-theme-text-secondary mb-1">Leg Hit Rate</div>
            <div
              className={`text-2xl font-bold ${legStats.legHitRate >= 55 ? 'text-theme-success' : legStats.legHitRate >= 50 ? 'text-gray-100' : 'text-theme-danger'}`}
            >
              {legStats.legHitRate}%
            </div>
          </div>
          <div className="bg-theme-surface/50 rounded-lg p-4">
            <div className="text-xs text-theme-text-secondary mb-1">Leg Record</div>
            <div className="text-lg font-semibold text-gray-100">
              {legStats.legsHit}W - {legStats.legsMissed}L
            </div>
            <div className="text-xs text-theme-text-tertiary mt-1">
              {legStats.totalLegs} total legs
            </div>
          </div>
          <div className="bg-theme-surface/50 rounded-lg p-4">
            <div className="text-xs text-theme-text-secondary mb-1">Over Hit Rate</div>
            {legStats.oversTotal > 0 ? (
              <>
                <div
                  className={`text-2xl font-bold ${legStats.overHitRate >= 55 ? 'text-theme-success' : legStats.overHitRate >= 50 ? 'text-gray-100' : 'text-theme-danger'}`}
                >
                  {legStats.overHitRate}%
                </div>
                <div className="text-xs text-theme-text-tertiary mt-1">
                  {legStats.oversHit}/{legStats.oversTotal} overs
                </div>
              </>
            ) : (
              <div className="text-2xl font-bold text-theme-text-tertiary">N/A</div>
            )}
          </div>
          <div className="bg-theme-surface/50 rounded-lg p-4">
            <div className="text-xs text-theme-text-secondary mb-1">Under Hit Rate</div>
            {legStats.undersTotal > 0 ? (
              <>
                <div
                  className={`text-2xl font-bold ${legStats.underHitRate >= 55 ? 'text-theme-success' : legStats.underHitRate >= 50 ? 'text-gray-100' : 'text-theme-danger'}`}
                >
                  {legStats.underHitRate}%
                </div>
                <div className="text-xs text-theme-text-tertiary mt-1">
                  {legStats.undersHit}/{legStats.undersTotal} unders
                </div>
              </>
            ) : (
              <div className="text-2xl font-bold text-theme-text-tertiary">N/A</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
